import React, { useEffect, useMemo, useState } from "react";

type TestStatus = "Published" | "Draft" | "Scheduled" | "Archived";
type TestCategory = "NEET" | "JEE Main" | "Class 8" | "Class 7" | "Foundation";

type TestSection = {
  id: number;
  name: string;
  subject: string;
  questions: number;
  marksPerQuestion: number;
};

type TestSeries = {
  id: number;
  title: string;
  category: TestCategory;
  course: string;
  duration: number;
  totalQuestions: number;
  totalMarks: number;
  negativeMarks: number;
  attempts: number;
  students: number;
  startDate: string;
  endDate: string;
  status: TestStatus;
  price: string;
  resultMode: "Instant" | "After End Date" | "Manual";
  proctoring: boolean;
  shuffleQuestions: boolean;
  sections: TestSection[];
  instructions: string;
  teacherName?: string; // Teacher name track karne ke liye
};

type TestForm = Omit<TestSeries, "id" | "attempts" | "students" | "teacherName">;

// Teen modes: list, form, aur naya 'questions' mode
type PageMode = "list" | "form" | "questions";

const emptyForm: TestForm = {
  title: "",
  category: "NEET",
  course: "NEET Complete Preparation",
  duration: 180,
  totalQuestions: 180,
  totalMarks: 720,
  negativeMarks: 1,
  startDate: "",
  endDate: "",
  status: "Draft",
  price: "Free",
  resultMode: "Instant",
  proctoring: true,
  shuffleQuestions: true,
  sections: [
    {
      id: 1,
      name: "Section A",
      subject: "Biology",
      questions: 45,
      marksPerQuestion: 4,
    },
  ],
  instructions:
    "Read all questions carefully. Negative marking may be applicable. Do not refresh the page during test.",
};

const categories = ["All", "NEET", "JEE Main", "Class 8", "Class 7", "Foundation"];
const statuses = ["All", "Published", "Draft", "Scheduled", "Archived"];

// ✅ Yahan Localhost wala API set kar diya hai
// const API_BASE = "http://localhost:9090/api/tests"; 
const API_BASE = "https://node-api.mediitacademy.com/api/tests";

const AdminTestSeries: React.FC = () => {
  const [tests, setTests] = useState<TestSeries[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  
  const [pageMode, setPageMode] = useState<PageMode>("list");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [activeTestForQs, setActiveTestForQs] = useState<TestSeries | null>(null);

  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [activeStatus, setActiveStatus] = useState("All");

  const [form, setForm] = useState<TestForm>(emptyForm);

  // Question Management State
  const [questions, setQuestions] = useState<any[]>([]);
  const [qForm, setQForm] = useState({ id: null, questionText: "", optionA: "", optionB: "", optionC: "", optionD: "", correctOption: "A", explanation: "" });

  const getToken = () => localStorage.getItem("token") || "";

  const authHeaders = () => {
    const token = getToken();
    return {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  const normalizeTest = (item: any): TestSeries => ({
    id: Number(item.id || 0),
    title: item.title || "",
    category: (item.category || "NEET") as TestCategory,
    course: item.course || "",
    duration: Number(item.duration || 0),
    totalQuestions: Number(item.totalQuestions || item.total_questions || 0),
    totalMarks: Number(item.totalMarks || item.total_marks || 0),
    negativeMarks: Number(item.negativeMarks ?? item.negative_marks ?? 0),
    attempts: Number(item.attempts || 0),
    students: Number(item.students || 0),
    startDate: item.startDate || item.start_date || "",
    endDate: item.endDate || item.end_date || "",
    status: (item.status || "Draft") as TestStatus,
    price: item.price || "Free",
    resultMode: (item.resultMode || item.result_mode || "Instant") as TestSeries["resultMode"],
    proctoring: Boolean(item.proctoring),
    shuffleQuestions: Boolean(item.shuffleQuestions ?? item.shuffle_questions ?? true),
    sections: Array.isArray(item.sections)
      ? item.sections.map((section: any, index: number) => ({
          id: Number(section.id || index + 1),
          name: section.name || `Section ${index + 1}`,
          subject: section.subject || "",
          questions: Number(section.questions || 0),
          marksPerQuestion: Number(section.marksPerQuestion || section.marks_per_question || 0),
        }))
      : [],
    instructions: item.instructions || "",
    teacherName: item.teacher_name || item.teacherName || "Admin",
  });

  const loadTests = async () => {
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams();
      if (activeCategory !== "All") params.set("category", activeCategory);
      if (activeStatus !== "All") params.set("status", activeStatus);
      if (search.trim()) params.set("search", search.trim());

      // Query params ko local API pe bhej rahe hain
      const url = params.toString() ? `${API_BASE}?${params.toString()}` : API_BASE;
      const response = await fetch(url, { headers: authHeaders() });

      if (!response.ok) throw new Error(`Test series API failed (${response.status})`);

      const data = await response.json();
      setTests(Array.isArray(data) ? data.map(normalizeTest) : []);
    } catch (err: any) {
      console.error("Test series loading error:", err);
      setError(err?.message || "Test series loading failed");
      setTests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      loadTests();
    }, 250);

    return () => window.clearTimeout(timer);
  }, [activeCategory, activeStatus, search]);

  const filteredTests = useMemo(() => {
    return tests.filter((test) => {
      const matchCategory = activeCategory === "All" || test.category === activeCategory;
      const matchStatus = activeStatus === "All" || test.status === activeStatus;
      const searchText = search.toLowerCase();
      const matchSearch =
        test.title.toLowerCase().includes(searchText) ||
        test.course.toLowerCase().includes(searchText) ||
        test.category.toLowerCase().includes(searchText) ||
        (test.teacherName && test.teacherName.toLowerCase().includes(searchText));

      return matchCategory && matchStatus && matchSearch;
    });
  }, [tests, search, activeCategory, activeStatus]);

  const openCreatePage = () => {
    setEditingId(null);
    setForm(emptyForm);
    setPageMode("form");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openEditPage = (test: TestSeries) => {
    setEditingId(test.id);
    setForm({
      title: test.title,
      category: test.category,
      course: test.course,
      duration: test.duration,
      totalQuestions: test.totalQuestions,
      totalMarks: test.totalMarks,
      negativeMarks: test.negativeMarks,
      startDate: test.startDate,
      endDate: test.endDate,
      status: test.status,
      price: test.price,
      resultMode: test.resultMode,
      proctoring: test.proctoring,
      shuffleQuestions: test.shuffleQuestions,
      sections: test.sections,
      instructions: test.instructions,
    });
    setPageMode("form");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const backToList = () => {
    setEditingId(null);
    setForm(emptyForm);
    setActiveTestForQs(null);
    setPageMode("list");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSectionChange = (id: number, field: keyof TestSection, value: string | number) => {
    setForm((prev) => ({
      ...prev,
      sections: prev.sections.map((section) =>
        section.id === id ? { ...section, [field]: value } : section
      ),
    }));
  };

  const addSection = () => {
    const nextId = form.sections.length > 0 ? Math.max(...form.sections.map((section) => section.id)) + 1 : 1;
    setForm((prev) => ({
      ...prev,
      sections: [
        ...prev.sections,
        { id: nextId, name: `Section ${String.fromCharCode(64 + nextId)}`, subject: "Physics", questions: 25, marksPerQuestion: 4 },
      ],
    }));
  };

  const removeSection = (id: number) => {
    if (form.sections.length === 1) {
      alert("Minimum 1 section required hai.");
      return;
    }
    setForm((prev) => ({
      ...prev,
      sections: prev.sections.filter((section) => section.id !== id),
    }));
  };

  const calculateFromSections = () => {
    const totalQuestions = form.sections.reduce((sum, section) => sum + Number(section.questions || 0), 0);
    const totalMarks = form.sections.reduce((sum, section) => sum + Number(section.questions || 0) * Number(section.marksPerQuestion || 0), 0);
    setForm((prev) => ({ ...prev, totalQuestions, totalMarks }));
  };

  const buildPayload = () => ({
    ...form,
    duration: Number(form.duration),
    totalQuestions: Number(form.totalQuestions),
    totalMarks: Number(form.totalMarks),
    negativeMarks: Number(form.negativeMarks || 0),
    sections: form.sections.map((section, index) => ({
      name: section.name,
      subject: section.subject,
      questions: Number(section.questions || 0),
      marksPerQuestion: Number(section.marksPerQuestion || 0),
      sectionOrder: index + 1,
    })),
  });

  const handleSaveTest = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.title.trim() || !form.course.trim() || !form.startDate || !form.endDate) {
      alert("Please fill test title, course, start date and end date.");
      return;
    }

    if (form.duration <= 0 || form.totalQuestions <= 0 || form.totalMarks <= 0) {
      alert("Duration, total questions and total marks valid hone chahiye.");
      return;
    }

    setSaving(true);
    setError("");

    try {
      const response = await fetch(editingId ? `${API_BASE}/${editingId}` : `${API_BASE}/create`, {
        method: editingId ? "PUT" : "POST",
        headers: authHeaders(),
        body: JSON.stringify(buildPayload()),
      });

      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || `Save failed (${response.status})`);
      }

      await loadTests();
      backToList();
    } catch (err: any) {
      console.error("Test series save error:", err);
      setError(err?.message || "Test series save failed");
      alert(err?.message || "Test series save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    const confirmDelete = window.confirm("Are you sure? Ye test series delete ho jayegi.");
    if (!confirmDelete) return;

    try {
      const response = await fetch(`${API_BASE}/${id}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      if (!response.ok) throw new Error(`Delete failed (${response.status})`);
      await loadTests();
    } catch (err: any) {
      console.error("Test series delete error:", err);
      alert(err?.message || "Delete failed");
    }
  };

  // --- QUESTION MANAGEMENT FUNCTIONS ---
  const openQuestionsPage = async (test: TestSeries) => {
    setActiveTestForQs(test);
    setPageMode("questions");
    loadQuestions(test.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const loadQuestions = async (testId: number) => {
    try {
      const res = await fetch(`${API_BASE}/${testId}/questions`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setQuestions(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error("Failed to load questions", err);
    }
  };

  const saveQuestion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeTestForQs) return;

    try {
      const url = qForm.id ? `${API_BASE}/question/${qForm.id}` : `${API_BASE}/${activeTestForQs.id}/question`;
      const method = qForm.id ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: authHeaders(),
        body: JSON.stringify(qForm)
      });
      
      if (!res.ok) throw new Error("Failed to save question");
      
      alert(qForm.id ? "Question updated!" : "Question added!");
      setQForm({ id: null, questionText: "", optionA: "", optionB: "", optionC: "", optionD: "", correctOption: "A", explanation: "" });
      loadQuestions(activeTestForQs.id);
    } catch (err) {
      alert("Error saving question.");
    }
  };

  const editQuestion = (q: any) => {
    const lines = q.question ? q.question.split("\n") : [];
    setQForm({
      id: q.id,
      questionText: lines[0] || "",
      optionA: lines[1]?.replace("A) ", "") || "",
      optionB: lines[2]?.replace("B) ", "") || "",
      optionC: lines[3]?.replace("C) ", "") || "",
      optionD: lines[4]?.replace("D) ", "") || "",
      correctOption: q.answer?.replace("Option ", "") || "A",
      explanation: q.explanation || ""
    });
  };

  const deleteQuestion = async (id: number) => {
    if (!window.confirm("Delete this question forever?")) return;
    try {
      await fetch(`${API_BASE}/question/${id}`, { method: "DELETE", headers: authHeaders() });
      if (activeTestForQs) loadQuestions(activeTestForQs.id);
    } catch (err) {
      alert("Delete failed");
    }
  };

  // --- RENDER FORM MODE ---
  if (pageMode === "form") {
    return (
      <div className="w-full max-w-full min-w-0 overflow-hidden space-y-5 sm:space-y-6">
        <section className="relative overflow-hidden rounded-[24px] bg-slate-950 p-5 text-white shadow-xl shadow-slate-300/40 sm:rounded-[30px] sm:p-8">
          <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-blue-500/30 blur-3xl" />
          <div className="absolute -bottom-24 left-1/2 h-72 w-72 rounded-full bg-cyan-400/20 blur-3xl" />

          <div className="relative flex min-w-0 flex-col justify-between gap-5 lg:flex-row lg:items-center">
            <div className="min-w-0">
              <div className="mb-4 inline-flex rounded-full border border-white/10 bg-white/10 px-4 py-2 text-xs font-black uppercase tracking-wide text-cyan-100">
                {editingId ? "Edit Test Series" : "Create Test Series"}
              </div>

              <h1 className="max-w-3xl text-2xl font-black tracking-tight sm:text-4xl">
                {editingId ? "Update test details" : "Create new mock test"}
              </h1>

              <p className="mt-3 max-w-2xl text-sm font-semibold leading-6 text-slate-300">
                Test ka course, schedule, duration, marks, sections, result mode
                aur security settings yaha se manage karo.
              </p>
            </div>

            <button
              type="button"
              onClick={backToList}
              className="w-fit shrink-0 rounded-2xl bg-white px-5 py-3 text-sm font-black text-slate-950 hover:bg-cyan-50"
            >
              ← Back to Test Series
            </button>
          </div>
        </section>

        {error && (
          <div className="rounded-[22px] border border-red-100 bg-red-50 p-4 text-sm font-black text-red-700">
            {error}
          </div>
        )}

        <form
          onSubmit={handleSaveTest}
          className="grid min-w-0 gap-6 2xl:grid-cols-[minmax(0,1fr)_340px]"
        >
          <section className="min-w-0 space-y-6">
            <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm sm:rounded-[28px] sm:p-6">
              <h2 className="text-xl font-black text-slate-950">
                Basic Test Details
              </h2>

              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <FormInput
                    label="Test Title"
                    value={form.title}
                    placeholder="Example: NEET Biology Full Mock Test - 01"
                    onChange={(value) => setForm({ ...form, title: value })}
                  />
                </div>

                <SelectInput
                  label="Category"
                  value={form.category}
                  options={["NEET", "JEE Main", "Class 8", "Class 7", "Foundation"]}
                  onChange={(value) => setForm({ ...form, category: value as TestCategory })}
                />

                <FormInput
                  label="Course Name"
                  value={form.course}
                  placeholder="Example: NEET Complete Preparation"
                  onChange={(value) => setForm({ ...form, course: value })}
                />

                <FormInput
                  label="Duration In Minutes"
                  type="number"
                  value={form.duration.toString()}
                  placeholder="180"
                  onChange={(value) => setForm({ ...form, duration: Number(value) })}
                />

                <FormInput
                  label="Total Questions"
                  type="number"
                  value={form.totalQuestions.toString()}
                  placeholder="180"
                  onChange={(value) => setForm({ ...form, totalQuestions: Number(value) })}
                />

                <FormInput
                  label="Total Marks"
                  type="number"
                  value={form.totalMarks.toString()}
                  placeholder="720"
                  onChange={(value) => setForm({ ...form, totalMarks: Number(value) })}
                />

                <FormInput
                  label="Negative Marks"
                  type="number"
                  value={form.negativeMarks.toString()}
                  placeholder="1"
                  onChange={(value) => setForm({ ...form, negativeMarks: Number(value) })}
                />
              </div>
            </div>

            <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm sm:rounded-[28px] sm:p-6">
              <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <div>
                  <h2 className="text-xl font-black text-slate-950">
                    Test Sections
                  </h2>
                  <p className="mt-1 text-sm font-semibold text-slate-500">
                    Subject wise sections and question distribution
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={calculateFromSections}
                    className="rounded-2xl bg-slate-100 px-4 py-2.5 text-xs font-black text-slate-700 hover:bg-slate-200"
                  >
                    Auto Calculate
                  </button>

                  <button
                    type="button"
                    onClick={addSection}
                    className="rounded-2xl bg-blue-600 px-4 py-2.5 text-xs font-black text-white hover:bg-blue-700"
                  >
                    + Add Section
                  </button>
                </div>
              </div>

              <div className="mt-5 space-y-4">
                {form.sections.map((section, index) => (
                  <div
                    key={section.id}
                    className="min-w-0 rounded-[22px] border border-slate-100 bg-slate-50 p-4 sm:rounded-[24px]"
                  >
                    <div className="mb-4 flex items-center justify-between gap-3">
                      <h3 className="font-black text-slate-950">
                        Section {index + 1}
                      </h3>

                      <button
                        type="button"
                        onClick={() => removeSection(section.id)}
                        className="shrink-0 rounded-xl bg-red-50 px-3 py-2 text-xs font-black text-red-600 hover:bg-red-100"
                      >
                        Remove
                      </button>
                    </div>

                    <div className="grid min-w-0 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                      <FormInput
                        label="Section Name"
                        value={section.name}
                        placeholder="Section A"
                        onChange={(value) => handleSectionChange(section.id, "name", value)}
                      />

                      <FormInput
                        label="Subject"
                        value={section.subject}
                        placeholder="Biology"
                        onChange={(value) => handleSectionChange(section.id, "subject", value)}
                      />

                      <FormInput
                        label="Questions"
                        type="number"
                        value={section.questions.toString()}
                        placeholder="45"
                        onChange={(value) => handleSectionChange(section.id, "questions", Number(value))}
                      />

                      <FormInput
                        label="Marks / Question"
                        type="number"
                        value={section.marksPerQuestion.toString()}
                        placeholder="4"
                        onChange={(value) => handleSectionChange(section.id, "marksPerQuestion", Number(value))}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm sm:rounded-[28px] sm:p-6">
              <h2 className="text-xl font-black text-slate-950">
                Instructions
              </h2>

              <textarea
                value={form.instructions}
                onChange={(e) => setForm({ ...form, instructions: e.target.value })}
                placeholder="Write test instructions..."
                rows={5}
                className="mt-5 w-full resize-none rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold text-slate-700 outline-none placeholder:text-slate-400 focus:border-blue-500 focus:bg-white"
              />
            </div>
          </section>

          <aside className="min-w-0 space-y-6">
            <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm sm:rounded-[28px]">
              <h2 className="text-xl font-black text-slate-950">
                Schedule & Publish
              </h2>

              <div className="mt-5 grid gap-4 sm:grid-cols-2 2xl:grid-cols-1">
                <FormInput
                  label="Start Date"
                  type="date"
                  value={form.startDate}
                  placeholder=""
                  onChange={(value) => setForm({ ...form, startDate: value })}
                />

                <FormInput
                  label="End Date"
                  type="date"
                  value={form.endDate}
                  placeholder=""
                  onChange={(value) => setForm({ ...form, endDate: value })}
                />

                <SelectInput
                  label="Status"
                  value={form.status}
                  options={["Published", "Draft", "Scheduled", "Archived"]}
                  onChange={(value) => setForm({ ...form, status: value as TestStatus })}
                />

                <SelectInput
                  label="Result Mode"
                  value={form.resultMode}
                  options={["Instant", "After End Date", "Manual"]}
                  onChange={(value) => setForm({ ...form, resultMode: value as TestForm["resultMode"] })}
                />

                <FormInput
                  label="Price"
                  value={form.price}
                  placeholder="Free or ₹199"
                  onChange={(value) => setForm({ ...form, price: value })}
                />
              </div>
            </div>

            <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm sm:rounded-[28px]">
              <h2 className="text-xl font-black text-slate-950">
                Security Settings
              </h2>

              <div className="mt-5 grid gap-3 sm:grid-cols-2 2xl:grid-cols-1">
                <ToggleRow
                  title="Enable Proctoring"
                  description="Tab switch and suspicious activity tracking"
                  checked={form.proctoring}
                  onChange={(value) => setForm({ ...form, proctoring: value })}
                />

                <ToggleRow
                  title="Shuffle Questions"
                  description="Every student gets different order"
                  checked={form.shuffleQuestions}
                  onChange={(value) => setForm({ ...form, shuffleQuestions: value })}
                />
              </div>
            </div>

            <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-xl sm:rounded-[28px] 2xl:sticky 2xl:bottom-5">
              <div className="grid gap-3 sm:grid-cols-3 2xl:grid-cols-1">
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-2xl bg-blue-600 px-5 py-3 text-sm font-black text-white shadow-lg shadow-blue-600/20 hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {saving ? "Saving..." : editingId ? "Update Test" : "Create Test"}
                </button>

                <button
                  type="button"
                  onClick={() => setForm({ ...form, status: "Draft" })}
                  className="rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-black text-slate-700 hover:bg-slate-50"
                >
                  Save as Draft
                </button>

                <button
                  type="button"
                  onClick={backToList}
                  className="rounded-2xl bg-slate-100 px-5 py-3 text-center text-sm font-black text-slate-700 hover:bg-slate-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          </aside>
        </form>
      </div>
    );
  }

  // --- RENDER QUESTIONS MANAGER MODE ---
  if (pageMode === "questions" && activeTestForQs) {
    return (
      <div className="w-full max-w-full min-w-0 overflow-hidden space-y-6">
        <section className="relative overflow-hidden rounded-[24px] bg-slate-950 p-5 text-white shadow-xl sm:rounded-[30px] sm:p-8">
          <div className="relative flex min-w-0 flex-col justify-between gap-5 lg:flex-row lg:items-center">
            <div>
              <div className="mb-4 inline-flex rounded-full border border-white/10 bg-white/10 px-4 py-2 text-xs font-black uppercase tracking-wide text-cyan-100">
                Question Manager
              </div>
              <h1 className="text-2xl font-black tracking-tight sm:text-4xl">{activeTestForQs.title}</h1>
              <p className="mt-2 text-sm text-slate-400">Created by: {activeTestForQs.teacherName}</p>
            </div>
            <button onClick={backToList} className="rounded-2xl bg-white px-5 py-3 text-sm font-black text-slate-950 hover:bg-cyan-50">
              ← Back to Tests
            </button>
          </div>
        </section>

        <div className="grid gap-6 xl:grid-cols-[400px_1fr]">
          <form onSubmit={saveQuestion} className="rounded-[30px] border border-slate-200 bg-white p-6 shadow-sm space-y-4 h-fit">
            <h3 className="text-xl font-black text-slate-950">{qForm.id ? "Edit Question" : "Add New Question"}</h3>
            <textarea value={qForm.questionText} onChange={e => setQForm({...qForm, questionText: e.target.value})} placeholder="Question Statement..." className="w-full rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm font-bold outline-none" rows={3} required />
            <div className="grid grid-cols-2 gap-3">
              <input value={qForm.optionA} onChange={e => setQForm({...qForm, optionA: e.target.value})} placeholder="Option A" className="h-11 rounded-xl border border-slate-200 px-3 text-xs font-bold" required />
              <input value={qForm.optionB} onChange={e => setQForm({...qForm, optionB: e.target.value})} placeholder="Option B" className="h-11 rounded-xl border border-slate-200 px-3 text-xs font-bold" required />
              <input value={qForm.optionC} onChange={e => setQForm({...qForm, optionC: e.target.value})} placeholder="Option C" className="h-11 rounded-xl border border-slate-200 px-3 text-xs font-bold" required />
              <input value={qForm.optionD} onChange={e => setQForm({...qForm, optionD: e.target.value})} placeholder="Option D" className="h-11 rounded-xl border border-slate-200 px-3 text-xs font-bold" required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <select value={qForm.correctOption} onChange={e => setQForm({...qForm, correctOption: e.target.value})} className="h-11 w-full rounded-xl border border-slate-200 px-3 text-xs font-bold">
                <option value="A">Correct: A</option><option value="B">Correct: B</option><option value="C">Correct: C</option><option value="D">Correct: D</option>
              </select>
              <input value={qForm.explanation} onChange={e => setQForm({...qForm, explanation: e.target.value})} placeholder="Explanation" className="h-11 rounded-xl border border-slate-200 px-3 text-xs font-bold" />
            </div>
            <div className="flex gap-2">
              <button type="submit" className="flex-1 rounded-2xl bg-blue-600 py-3 text-xs font-black text-white hover:bg-blue-700">{qForm.id ? "Update" : "Save"}</button>
              {qForm.id && <button type="button" onClick={() => setQForm({ id: null, questionText: "", optionA: "", optionB: "", optionC: "", optionD: "", correctOption: "A", explanation: "" })} className="rounded-2xl bg-slate-200 px-4 text-xs font-black">Cancel</button>}
            </div>
          </form>

          <div className="rounded-[30px] border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="text-xl font-black text-slate-950 mb-5">Questions List ({questions.length})</h3>
            <div className="space-y-4">
              {questions.map((q, idx) => (
                <div key={q.id} className="rounded-2xl border border-slate-100 bg-[#f8fbff] p-5">
                  <div className="flex justify-between items-center mb-2">
                    <span className="rounded-full bg-blue-100 px-3 py-1 text-[10px] font-black text-blue-700">Q {idx + 1}</span>
                    <div className="flex gap-2">
                      <button onClick={() => editQuestion(q)} className="text-[10px] font-black text-slate-600 bg-slate-200 px-2 py-1 rounded">Edit</button>
                      <button onClick={() => deleteQuestion(q.id)} className="text-[10px] font-black text-rose-600 bg-rose-100 px-2 py-1 rounded">Delete</button>
                    </div>
                  </div>
                  <p className="text-sm font-black text-slate-900 whitespace-pre-line">{q.question}</p>
                  <p className="mt-2 text-xs font-bold text-emerald-700">Answer: {q.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- RENDER LIST MODE ---
  return (
    <div className="w-full max-w-full min-w-0 overflow-hidden space-y-5 sm:space-y-6">
      <section className="relative overflow-hidden rounded-[24px] bg-slate-950 p-5 text-white shadow-xl shadow-slate-300/40 sm:rounded-[30px] sm:p-8">
        <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-blue-500/30 blur-3xl" />
        <div className="absolute -bottom-24 left-1/2 h-72 w-72 rounded-full bg-cyan-400/20 blur-3xl" />

        <div className="relative flex min-w-0 flex-col justify-between gap-5 xl:flex-row xl:items-center">
          <div className="min-w-0">
            <div className="mb-4 inline-flex rounded-full border border-white/10 bg-white/10 px-4 py-2 text-xs font-black uppercase tracking-wide text-cyan-100">
              Test Series Management
            </div>

            <h1 className="max-w-3xl text-2xl font-black tracking-tight sm:text-4xl">
              Manage mock tests and test series
            </h1>

            <p className="mt-3 max-w-2xl text-sm font-semibold leading-6 text-slate-300">
              Admin yaha se test create, schedule, publish, sections, marks,
              result mode aur security settings manage kar sakta hai.
            </p>
          </div>

          <div className="flex shrink-0 flex-wrap gap-3">
            <button
              onClick={loadTests}
              className="rounded-2xl border border-white/15 bg-white/10 px-5 py-3 text-sm font-black text-white hover:bg-white/15"
            >
              Refresh
            </button>

            <button className="rounded-2xl border border-white/15 bg-white/10 px-5 py-3 text-sm font-black text-white hover:bg-white/15">
              Export Report
            </button>

            <button
              onClick={openCreatePage}
              className="rounded-2xl bg-white px-5 py-3 text-sm font-black text-slate-950 hover:bg-cyan-50"
            >
              + Create Test
            </button>
          </div>
        </div>
      </section>

      <section className="grid min-w-0 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard title="Total Tests" value={tests.length.toString()} note="+12 this month" />
        <StatCard title="Published" value={tests.filter((t) => t.status === "Published").length.toString()} note="Live tests" />
        <StatCard title="Total Attempts" value={tests.reduce((sum, t) => sum + t.attempts, 0).toLocaleString()} note="+28.5% growth" />
        <StatCard title="Avg Completion" value="74%" note="Good performance" />
      </section>

      <section className="min-w-0 rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm sm:rounded-[28px]">
        <div className="grid min-w-0 gap-5 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-[1.2fr_1fr_1.5fr]">
          <FilterButtons title="Category" options={categories} active={activeCategory} onChange={setActiveCategory} />
          <FilterButtons title="Status" options={statuses} active={activeStatus} onChange={setActiveStatus} />
          <div className="min-w-0 md:col-span-2 xl:col-span-1">
            <p className="mb-2 text-xs font-black uppercase tracking-wide text-slate-400">Search</p>
            <div className="flex h-12 min-w-0 items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4">
              <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0 text-slate-400" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 21l-4.35-4.35M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z" />
              </svg>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search test title, course, teacher..."
                className="min-w-0 flex-1 bg-transparent text-sm font-semibold outline-none placeholder:text-slate-400"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="grid min-w-0 gap-6 2xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0 rounded-[24px] border border-slate-200 bg-white shadow-sm sm:rounded-[28px]">
          <div className="flex flex-col justify-between gap-4 border-b border-slate-100 p-5 sm:flex-row sm:items-center">
            <div>
              <h2 className="text-xl font-black text-slate-950">All Test Series</h2>
              <p className="mt-1 text-sm font-semibold text-slate-500">{filteredTests.length} tests found</p>
            </div>
            <button onClick={openCreatePage} className="w-fit rounded-2xl bg-blue-600 px-4 py-2.5 text-xs font-black text-white hover:bg-blue-700">
              + Create New
            </button>
          </div>

          <div className="hidden max-w-full overflow-x-auto 2xl:block">
            <table className="w-full min-w-[900px] text-left">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50 text-xs font-black uppercase tracking-wide text-slate-400">
                  <th className="px-5 py-4">Test</th>
                  <th className="px-5 py-4">Created By</th>
                  <th className="px-5 py-4">Questions</th>
                  <th className="px-5 py-4">Attempts</th>
                  <th className="px-5 py-4">Status</th>
                  <th className="px-5 py-4 text-right">Action</th>
                </tr>
              </thead>

              <tbody>
                {filteredTests.map((test) => (
                  <tr key={test.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                    <td className="px-5 py-4">
                      <p className="max-w-sm text-sm font-black leading-5 text-slate-950">{test.title}</p>
                      <p className="mt-1 text-xs font-bold text-slate-400">{test.category} • {test.course} • {test.price}</p>
                    </td>

                    <td className="px-5 py-4 text-sm font-black text-slate-700">
                      {test.teacherName}
                    </td>

                    <td className="px-5 py-4">
                      <button onClick={() => openQuestionsPage(test)} className="rounded-lg bg-blue-50 px-3 py-1.5 text-xs font-black text-blue-600 hover:bg-blue-100">
                        Manage Qs 📝
                      </button>
                    </td>

                    <td className="px-5 py-4">
                      <p className="text-sm font-black text-slate-800">{test.attempts.toLocaleString()}</p>
                      <p className="text-xs font-bold text-slate-400">{test.students.toLocaleString()} enrolled</p>
                    </td>

                    <td className="px-5 py-4"><StatusBadge status={test.status} /></td>

                    <td className="px-5 py-4">
                      <div className="flex justify-end gap-2">
                        <button onClick={() => openEditPage(test)} className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-black text-slate-600 hover:bg-slate-100">Edit</button>
                        <button onClick={() => handleDelete(test.id)} className="rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-xs font-black text-red-600 hover:bg-red-100">Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid gap-4 p-4 sm:p-5 2xl:hidden">
            {filteredTests.map((test) => (
              <div key={test.id} className="min-w-0 rounded-[22px] border border-slate-100 bg-slate-50 p-4">
                <div className="mb-3 flex flex-wrap gap-2">
                  <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-black text-blue-700">{test.category}</span>
                  <StatusBadge status={test.status} />
                </div>
                <h3 className="break-words text-sm font-black leading-5 text-slate-950">{test.title}</h3>
                <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  <InfoBox label="Course" value={test.course} />
                  <InfoBox label="Created By" value={test.teacherName || "Admin"} />
                </div>
                <div className="mt-4 flex flex-wrap gap-3">
                  <button onClick={() => openQuestionsPage(test)} className="rounded-2xl bg-blue-600 px-4 py-2.5 text-xs font-black text-white hover:bg-blue-700">Manage Qs 📝</button>
                  <button onClick={() => openEditPage(test)} className="rounded-2xl bg-slate-950 px-4 py-2.5 text-xs font-black text-white hover:bg-slate-800">Edit</button>
                  <button onClick={() => handleDelete(test.id)} className="rounded-2xl bg-red-50 px-4 py-2.5 text-xs font-black text-red-600 hover:bg-red-100">Delete</button>
                </div>
              </div>
            ))}
            {filteredTests.length === 0 && (
              <div className="rounded-2xl bg-slate-50 p-6 text-center text-sm font-bold text-slate-500">No test series found.</div>
            )}
          </div>
        </div>

        <aside className="min-w-0 space-y-6">
          <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm sm:rounded-[28px]">
            <h2 className="text-xl font-black text-slate-950">Admin Tools</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 2xl:grid-cols-1">
              {["Create Full Mock Test", "Create Chapter Test", "Assign Question Set", "Bulk Import Tests", "Publish Result", "Download Report"].map((item) => (
                <button key={item} className="group flex items-center justify-between rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3 text-left text-sm font-black text-slate-700 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700">
                  <span className="break-words">{item}</span>
                  <span className="shrink-0 transition group-hover:translate-x-1">→</span>
                </button>
              ))}
            </div>
          </div>
          <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm sm:rounded-[28px]">
            <h2 className="text-xl font-black text-slate-950">Test Performance</h2>
            <div className="mt-5 space-y-4">
              <QualityItem label="Completion Rate" value="74%" color="bg-blue-600" />
              <QualityItem label="Avg Accuracy" value="68%" color="bg-emerald-500" />
              <QualityItem label="Drop Rate" value="18%" color="bg-orange-500" />
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
};

// --- HELPER COMPONENTS ---

const StatCard = ({ title, value, note }: { title: string; value: string; note: string; }) => {
  return (
    <div className="min-w-0 rounded-[22px] border border-slate-200 bg-white p-5 shadow-sm sm:rounded-[24px]">
      <p className="text-sm font-bold text-slate-500">{title}</p>
      <h2 className="mt-2 text-3xl font-black text-slate-950">{value}</h2>
      <p className="mt-1 text-xs font-bold text-emerald-600">{note}</p>
    </div>
  );
};

const FilterButtons = ({ title, options, active, onChange }: { title: string; options: string[]; active: string; onChange: (value: string) => void; }) => {
  return (
    <div className="min-w-0">
      <p className="mb-2 text-xs font-black uppercase tracking-wide text-slate-400">{title}</p>
      <div className="flex flex-wrap gap-2">
        {options.map((item) => (
          <button
            key={item}
            onClick={() => onChange(item)}
            className={`rounded-2xl px-3 py-2 text-xs font-black transition ${
              active === item ? "bg-slate-950 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            {item}
          </button>
        ))}
      </div>
    </div>
  );
};

const StatusBadge = ({ status }: { status: TestStatus }) => {
  return (
    <span className={`inline-flex shrink-0 rounded-full px-3 py-1 text-xs font-black ${
        status === "Published" ? "bg-emerald-50 text-emerald-700" :
        status === "Scheduled" ? "bg-blue-50 text-blue-700" :
        status === "Draft" ? "bg-orange-50 text-orange-700" : "bg-slate-100 text-slate-600"
      }`}>
      {status}
    </span>
  );
};

const InfoBox = ({ label, value }: { label: string; value: string }) => {
  return (
    <div className="min-w-0 rounded-2xl bg-white p-3">
      <p className="text-xs font-bold text-slate-400">{label}</p>
      <p className="mt-1 break-words text-sm font-black text-slate-900">{value}</p>
    </div>
  );
};

const QualityItem = ({ label, value, color }: { label: string; value: string; color: string; }) => {
  return (
    <div>
      <div className="mb-2 flex justify-between gap-3 text-sm font-black text-slate-700">
        <span>{label}</span><span>{value}</span>
      </div>
      <div className="h-2 rounded-full bg-slate-100">
        <div className={`h-2 rounded-full ${color}`} style={{ width: value }} />
      </div>
    </div>
  );
};

const FormInput = ({ label, value, onChange, placeholder, type = "text" }: { label: string; value: string; placeholder: string; type?: string; onChange: (value: string) => void; }) => {
  return (
    <div className="min-w-0">
      <label className="mb-2 block text-xs font-black uppercase tracking-wide text-slate-500">{label}</label>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold text-slate-700 outline-none placeholder:text-slate-400 focus:border-blue-500 focus:bg-white"
      />
    </div>
  );
};

const SelectInput = ({ label, value, options, onChange }: { label: string; value: string; options: string[]; onChange: (value: string) => void; }) => {
  return (
    <div className="min-w-0">
      <label className="mb-2 block text-xs font-black uppercase tracking-wide text-slate-500">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold text-slate-700 outline-none focus:border-blue-500 focus:bg-white"
      >
        {options.map((item) => (
          <option key={item}>{item}</option>
        ))}
      </select>
    </div>
  );
};

const ToggleRow = ({ title, description, checked, onChange }: { title: string; description: string; checked: boolean; onChange: (value: boolean) => void; }) => {
  return (
    <button type="button" onClick={() => onChange(!checked)} className="flex w-full min-w-0 items-center justify-between gap-4 rounded-2xl border border-slate-100 bg-slate-50 p-4 text-left hover:bg-slate-100">
      <div className="min-w-0">
        <p className="break-words text-sm font-black text-slate-950">{title}</p>
        <p className="mt-1 break-words text-xs font-bold text-slate-400">{description}</p>
      </div>
      <span className={`flex h-7 w-12 shrink-0 items-center rounded-full p-1 transition ${checked ? "bg-blue-600" : "bg-slate-300"}`}>
        <span className={`h-5 w-5 rounded-full bg-white transition ${checked ? "translate-x-5" : "translate-x-0"}`} />
      </span>
    </button>
  );
};

export default AdminTestSeries;